import { Building2, UploadCloud, Download, Loader2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { DepartmentsTab } from './AccessControlPage';

/**
 * Standalone Departments page under Operations (v33).
 * Reuses the DepartmentsTab implementation that's already exported from
 * AccessControlPage so the two views stay in sync.
 *
 * v38.11 — Added "Bulk Department Upload" section. Each hospital uploads its
 * own department list (admin/clinical units used to derive Employee IDs).
 * The list is hospital-scoped; no cross-tenant replication.
 */
export default function DepartmentsPage() {
  const { user, hasPermission } = useAuth();
  const canSee = user?.is_super_admin || hasPermission('users.view') || hasPermission('roles.view');
  const canBulkUpload = user?.is_super_admin || hasPermission('users.create') || hasPermission('roles.create');

  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  if (!canSee) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="departments-restricted">
        <Card className="max-w-lg w-full"><CardHeader><CardTitle>Access Restricted</CardTitle></CardHeader>
          <CardContent>You don&apos;t have permission to view departments.</CardContent></Card>
      </div>
    );
  }

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/departments/bulk-upload/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'department-import-template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleBulkUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Pick a CSV / XLSX file first.'); return; }
    setUploading(true);
    setResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/departments/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      toast.success(`Created ${res.data.created.length} · Skipped ${res.data.skipped.length}`);
      // Force the DepartmentsTab below to refresh by bumping a tiny key prop via reload event.
      window.dispatchEvent(new CustomEvent('departments:refresh'));
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.errors) {
        toast.error(detail.message || 'Validation failed');
        setResult({ errors: detail.errors });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Upload failed');
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4" data-testid="departments-page">
      <div className="flex items-center gap-2">
        <Building2 className="text-emerald-600" />
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Departments</h1>
          <p className="text-sm text-slate-500">Define the departments your hospital operates. Staff department dropdown reads from this list.</p>
        </div>
      </div>

      {canBulkUpload && (
        <Card data-testid="bulk-dept-upload-card" className="border-emerald-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <UploadCloud className="h-4 w-4 text-emerald-600" />
              Bulk Department Upload
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert className="bg-emerald-50/40 border-emerald-100 mb-4">
              <AlertDescription className="text-xs text-slate-700 leading-relaxed">
                Upload a CSV / XLSX with columns <code>name</code>, <code>code</code> (required) and <code>description</code> (optional).
                Codes drive the Employee-ID format <code>&lt;PREFIX&gt;&lt;CODE&gt;&lt;P|C|T&gt;&lt;SERIAL&gt;</code>.
                <strong className="text-emerald-700"> Each hospital uploads its own list — no cross-tenant replication.</strong>
              </AlertDescription>
            </Alert>
            <div className="flex flex-wrap gap-3 items-center">
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx"
                data-testid="bulk-dept-file-input"
                className="text-sm file:mr-3 file:px-3 file:py-2 file:rounded-md file:border-0 file:bg-emerald-100 file:text-emerald-800 hover:file:bg-emerald-200"
              />
              <Button onClick={downloadTemplate} variant="outline" size="sm" data-testid="bulk-dept-template-btn">
                <Download className="h-3.5 w-3.5 mr-1.5" /> Template
              </Button>
              <Button
                onClick={handleBulkUpload}
                disabled={uploading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                size="sm"
                data-testid="bulk-dept-upload-btn"
              >
                {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5 mr-1.5" />}
                {uploading ? 'Uploading…' : 'Upload'}
              </Button>
            </div>

            {result?.errors && (
              <div className="mt-4 max-h-48 overflow-y-auto text-xs font-mono space-y-1" data-testid="bulk-dept-errors">
                {result.errors.map((e, i) => (
                  <div key={i} className="text-rose-700 border-b border-rose-100 py-1">Row {e.row}: {e.error}</div>
                ))}
              </div>
            )}
            {result?.created && (
              <div className="mt-4 rounded-xl border-2 border-emerald-300 bg-emerald-50/70 p-4 shadow-inner" data-testid="bulk-dept-result">
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-emerald-200">
                  <p className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5">
                    <UploadCloud className="h-4 w-4" />
                    Upload Successful · {result.created.length} created{result.skipped?.length > 0 ? ` · ${result.skipped.length} skipped` : ''}
                  </p>
                  <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Saved to this hospital</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs max-h-56 overflow-y-auto">
                  {result.created.map((c) => (
                    <div key={c.id} className="flex justify-between border-b border-emerald-100/60 py-1">
                      <span className="text-slate-700">{c.name}</span>
                      <span className="font-mono text-emerald-700 font-semibold">{c.code}</span>
                    </div>
                  ))}
                  {result.skipped?.length > 0 && result.skipped.map((s, i) => (
                    <div key={`sk-${i}`} className="flex justify-between border-b border-amber-100 py-1 col-span-2">
                      <span className="text-slate-500">Row {s.row}: {s.code}</span>
                      <span className="text-amber-700 text-[11px]">{s.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <DepartmentsTab />
    </div>
  );
}
